import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import { FilePlus, Loader2, User, Megaphone, Briefcase, Calendar, Users, Search, AlertTriangle, Info, ExternalLink } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Check } from "lucide-react";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useNavigate } from "react-router-dom";

interface AddKolTrackerDataDialogProps {
  onDataAdded?: () => void;
  hasFullAccess?: boolean;
  currentUserName?: string;
}

export function AddKolTrackerDataDialog({ 
  onDataAdded,
  hasFullAccess = true,
  currentUserName = ""
}: AddKolTrackerDataDialogProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const [kolDbOptions, setKolDbOptions] = useState<{ value: string; label: string; social_media_handler?: string }[]>([]);
  const [filteredKolOptions, setFilteredKolOptions] = useState<{ value: string; label: string; social_media_handler?: string }[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [picOptions, setPicOptions] = useState<{ value: string; label: string }[]>([]);
  const [showKolDropdown, setShowKolDropdown] = useState(false);
  const [contentIdOptions, setContentIdOptions] = useState<string[]>([]);
  const [actualUserName, setActualUserName] = useState<string>('');
  
  // Duplicate validation states
  const [duplicatePostLink, setDuplicatePostLink] = useState(false);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [duplicateEntry, setDuplicateEntry] = useState<any | null>(null);
  
  const [formData, setFormData] = useState({
    name: "",
    kol_db_id: "",
    type: "",
    channel: "",
    platform: "",
    post_type: "",
    post_link: "",
    content_views: 0,
    campaign_bu: "", // Using campaign_bu for content_id
    pic: [] as string[],
    posting_date: "",
    language: "",
    tier: "",
    integration_enable: false
  });

  const [postingDate, setPostingDate] = useState<Date | null>(null);

  const PLATFORM_OPTIONS = ['Telegram', 'Youtube', 'Blog', 'Twitter'];
  const TYPE_OPTIONS = [
    'Twitter - QT', 'Twitter - Tweet', 'Twitter - RT', 'Twitter - Thread/long post',
    'Telegram - post', 'Youtube - full video', 'Youtube - integration', 
    'Naver - Blog', 'Blog or News', 'PR', 'AMA', 'Others'
  ];
  const POST_TYPE_OPTIONS = ['Airdrop', 'Defi', 'General'];
  const TIER_OPTIONS = ['Tier 0', 'Tier 1', 'Tier 2', 'Tier 3'];
  const LANGUAGE_OPTIONS = ['CN', 'EN', 'ES', 'ID', 'IN', 'JP', 'KR', 'RU', 'TR', 'VN'];

  // Duplicate post link validation function
  const checkPostLinkDuplicate = async (postLink: string) => {
    if (!postLink.trim()) {
      setDuplicatePostLink(false);
      setDuplicateEntry(null);
      return;
    }
    
    setIsCheckingDuplicate(true);
    try {
      const { data, error } = await supabase
        .from('kol_tracker')
        .select('id, name, channel, posting_date, created_at')
        .eq('post_link', postLink.trim())
        .limit(1);
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        setDuplicatePostLink(true);
        setDuplicateEntry(data[0]);
        console.log('Duplicate post link found:', data[0]);
      } else {
        setDuplicatePostLink(false);
        setDuplicateEntry(null);
        console.log('No duplicate post link found');
      }
    } catch (error) {
      console.error('Error checking duplicate post link:', error);
      setDuplicatePostLink(false);
      setDuplicateEntry(null);
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  // Debounced validation effect for post link
  useEffect(() => {
    if (formData.post_link.trim()) {
      const debounceTimer = setTimeout(() => {
        checkPostLinkDuplicate(formData.post_link);
      }, 500); // 500ms debounce
      
      return () => clearTimeout(debounceTimer);
    } else {
      // Clear validation state when post link is empty
      setDuplicatePostLink(false);
      setDuplicateEntry(null);
      setIsCheckingDuplicate(false);
    }
  }, [formData.post_link]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch KOL DB data
        const { data: kolData, error: kolError } = await supabase
          .from('kol_db')
          .select('id, creator_name, social_media_handler');
          
        if (kolError) throw kolError;
        
        if (kolData) {
          const options = kolData.map(item => ({
            value: item.id,
            label: item.creator_name,
            social_media_handler: item.social_media_handler
          }));
          setKolDbOptions(options);
          setFilteredKolOptions(options);
        }

        // Fetch PIC data and get the correct user name
        const { data: picData, error: picError } = await supabase
          .from('kol_pic')
          .select('name, id')
          .order('name');
        
        if (picError) throw picError;
        
        if (picData) {
          const options = picData.map(pic => ({
            value: pic.name,
            label: pic.name
          }));
          setPicOptions(options);
          
          // Get the actual user name with correct capitalization from kol_pic table
          if (!hasFullAccess) {
            const { data: userData, error: userError } = await supabase.auth.getUser();
            if (userData.user) {
              const currentUserPic = picData.find(pic => pic.id === userData.user.id);
              if (currentUserPic) {
                const correctUserName = currentUserPic.name;
                setActualUserName(correctUserName);
                setFormData(prev => ({
                  ...prev,
                  pic: [correctUserName]
                }));
                console.log('Setting correct user name for limited user:', correctUserName);
              }
            }
          }
        }

        // Fetch Content IDs from topics_business_units table content_id column
        const { data: contentIdData, error: contentIdError } = await supabase
          .from('topics_business_units')
          .select('content_id')
          .not('content_id', 'is', null)
          .not('content_id', 'eq', '');
        
        if (contentIdError) throw contentIdError;
        
        if (contentIdData) {
          // Get unique content IDs and sort them
          const uniqueContentIds = [...new Set(contentIdData.map(item => item.content_id))].sort();
          setContentIdOptions(uniqueContentIds);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };
    
    if (open) {
      fetchData();
    }
  }, [open, hasFullAccess]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredKolOptions(kolDbOptions);
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      const filtered = kolDbOptions.filter(kol => 
        kol.label.toLowerCase().includes(lowerSearchTerm) ||
        (kol.social_media_handler && kol.social_media_handler.toLowerCase().includes(lowerSearchTerm))
      );
      setFilteredKolOptions(filtered);
    }
  }, [searchTerm, kolDbOptions]);

  useEffect(() => {
    if (formData.kol_db_id) {
      const fetchKolDetails = async () => {
        try {
          const { data, error } = await supabase
            .from('kol_db')
            .select('creator_name, social_media_handler, social_media_type, tier, pic_id')
            .eq('id', formData.kol_db_id)
            .single();
            
          if (error) throw error;
          
          if (data) {
            // Convert pic_id array to pic names array
            let picArray: string[] = [];
            if (data.pic_id && Array.isArray(data.pic_id) && data.pic_id.length > 0) {
              // Convert Json[] to string[] for the query
              const picIds = data.pic_id.map(id => String(id));
              
              // Fetch the actual names for the pic IDs
              const { data: picData, error: picError } = await supabase
                .from('kol_pic')
                .select('name')
                .in('id', picIds);
              
              if (!picError && picData) {
                picArray = picData.map(pic => pic.name);
              }
            }
            
            // For limited users, ensure current user is always in PIC using the correct name
            let finalPicArray = picArray;
            if (!hasFullAccess && actualUserName) {
              // Ensure current user is in the array with correct capitalization
              if (!picArray.includes(actualUserName)) {
                finalPicArray = [...picArray, actualUserName];
              } else {
                finalPicArray = picArray;
              }
              console.log('Limited user KOL selection - Final PIC array:', finalPicArray);
              console.log('Actual user name used:', actualUserName);
            }
            
            setFormData(prev => ({
              ...prev,
              name: data.creator_name,
              channel: data.social_media_handler || '',
              platform: data.social_media_type || '',
              tier: data.tier || '',
              pic: finalPicArray
            }));
          }
        } catch (err) {
          console.error("Error fetching KOL details:", err);
        }
      };
      
      fetchKolDetails();
    }
  }, [formData.kol_db_id, hasFullAccess, actualUserName]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = value === '' ? 0 : Number(value);
    setFormData((prev) => ({ ...prev, [name]: numValue }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePicChange = (values: string[]) => {
    // For limited users, ensure current user is always in the PIC array using correct name
    let finalValues = values;
    if (!hasFullAccess && actualUserName) {
      if (!values.includes(actualUserName)) {
        finalValues = [...values, actualUserName];
      }
    }
    setFormData((prev) => ({ ...prev, pic: finalValues }));
  };

  const handleToggleChange = (checked: boolean) => {
    setFormData((prev) => ({ ...prev, integration_enable: checked }));
  };
  
  const handlePostingDateChange = (date: Date | null) => {
    setPostingDate(date);
    setFormData(prev => ({
      ...prev,
      posting_date: date ? date.toISOString().split('T')[0] : ''
    }));
  };

  // Reset form function to clear validation states
  const resetForm = () => {
    setFormData({
      name: "",
      kol_db_id: "",
      type: "",
      channel: "",
      platform: "",
      post_type: "",
      post_link: "",
      content_views: 0,
      campaign_bu: "",
      pic: !hasFullAccess && actualUserName ? [actualUserName] : [],
      posting_date: "",
      language: "",
      tier: "",
      integration_enable: false
    });
    setPostingDate(null);
    setDuplicatePostLink(false);
    setDuplicateEntry(null);
    setIsCheckingDuplicate(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Form submission attempted');
    console.log('Duplicate post link state:', duplicatePostLink);
    console.log('Is checking duplicate:', isCheckingDuplicate);
    console.log('Post link value:', formData.post_link);
    
    if (!formData.name) {
      toast({
        title: "Required Field Missing",
        description: "Creator name is required",
        variant: "destructive",
      });
      return;
    }

    // Wait for any ongoing duplicate check to complete
    if (isCheckingDuplicate) {
      console.log('Still checking for duplicates, waiting...');
      toast({
        title: "Validation in Progress",
        description: "Please wait while we validate the post link.",
        variant: "default",
      });
      return;
    }

    // Prevent submission if duplicate post link exists
    if (duplicatePostLink) {
      console.log('Preventing submission due to duplicate post link');
      toast({
        title: "Duplicate Post Link",
        description: "This posting link already exists in the database. Please use a different link.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const picArray = Array.isArray(formData.pic) ? formData.pic : 
                      (formData.pic ? [formData.pic] : []);
      
      // For limited users, ALWAYS ensure their correct name is in the PIC array
      let finalPicArray = picArray;
      if (!hasFullAccess && actualUserName) {
        if (!picArray.includes(actualUserName)) {
          finalPicArray = [...picArray, actualUserName];
        }
        console.log('Limited user submission - Final PIC array:', finalPicArray);
        console.log('Actual user name:', actualUserName);
      }
      
      // Convert campaign_bu to proper format for database storage
      const campaignBuValue = formData.campaign_bu ? [formData.campaign_bu] : [];
      
      console.log('Submitting data:', {
        name: formData.name,
        kol_db_id: formData.kol_db_id || null,
        type: formData.type,
        channel: formData.channel,
        platform: formData.platform,
        post_type: formData.post_type,
        post_link: formData.post_link,
        content_views: formData.content_views,
        campaign_bu: campaignBuValue,
        pic: finalPicArray,
        posting_date: formData.posting_date || null,
        language: formData.language,
        tier: formData.tier,
        integration_enable: formData.integration_enable
      });
      
      const { error } = await supabase
        .from('kol_tracker')
        .insert({
          name: formData.name,
          kol_db_id: formData.kol_db_id || null,
          type: formData.type,
          channel: formData.channel,
          platform: formData.platform,
          post_type: formData.post_type,
          post_link: formData.post_link,
          content_views: formData.content_views,
          campaign_bu: campaignBuValue,
          pic: finalPicArray,
          posting_date: formData.posting_date || null,
          language: formData.language,
          tier: formData.tier,
          integration_enable: formData.integration_enable
        });

      if (error) {
        console.error('Insert error:', error);
        throw error;
      }

      toast({
        title: "Success",
        description: "KOL tracker data added successfully",
      });
      
      resetForm();
      
      if (onDataAdded) onDataAdded();
      setOpen(false);
    } catch (error) {
      console.error("Error adding data:", error);
      toast({
        title: "Error",
        description: "Failed to add KOL tracker data",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKolSelect = (id: string) => {
    setFormData(prev => ({ ...prev, kol_db_id: id }));
    setShowKolDropdown(false);
  };

  const selectedKolName = formData.kol_db_id ? 
    kolDbOptions.find(kol => kol.value === formData.kol_db_id)?.label + 
    (kolDbOptions.find(kol => kol.value === formData.kol_db_id)?.social_media_handler ? 
      ` (@${kolDbOptions.find(kol => kol.value === formData.kol_db_id)?.social_media_handler?.replace('@', '')})` : '') : 
    'Select KOL';

  const handleNavigateToDataImport = () => {
    setOpen(false);
    navigate('/kol-tracker?tab=data-import-v2');
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      setOpen(newOpen);
      if (!newOpen) {
        resetForm();
      }
    }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-9">
          <FilePlus className="h-4 w-4 mr-2" />
          Add Entry
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-xl font-semibold text-gray-800 dark:text-gray-100">
            Add New KOL Tracker Entry
          </DialogTitle>
          <DialogDescription className="text-gray-600 dark:text-gray-300">
            Create a new entry to track KOL posting activities and campaign performance.
          </DialogDescription>
        </DialogHeader>

        {/* Data Import Announcement */}
        <Alert className="mb-4 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-blue-700 dark:text-blue-300">
            <div className="flex items-start justify-between gap-3">
              <div>
                <strong>New Data Import Feature Available!</strong><br/>
                Save time by using our enhanced Data Import functionality to easily import multiple KOL entries at once instead of adding them one by one.
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNavigateToDataImport}
                className="shrink-0 text-blue-600 border-blue-300 hover:bg-blue-100 dark:text-blue-400 dark:border-blue-600 dark:hover:bg-blue-900/30"
              >
                Try Import <ExternalLink className="ml-1 h-3 w-3" />
              </Button>
            </div>
          </AlertDescription>
        </Alert>

        {/* Duplicate Post Link Warning */}
        {duplicatePostLink && duplicateEntry && (
          <Alert className="mb-4 border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
            <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
            <AlertTitle className="text-red-800 dark:text-red-200">
              Duplicate Post Link Detected
            </AlertTitle>
            <AlertDescription className="text-red-700 dark:text-red-300">
              This posting link already exists in the database:
              <div className="mt-1 text-sm">
                <strong>Creator:</strong> {duplicateEntry.name || 'Not specified'}<br/>
                <strong>Channel:</strong> {duplicateEntry.channel || 'Not specified'}<br/>
                <strong>Date:</strong> {duplicateEntry.posting_date ? new Date(duplicateEntry.posting_date).toLocaleDateString() : 'Not specified'}
              </div>
            </AlertDescription>
          </Alert>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Management Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <Users className="h-4 w-4 text-indigo-500" />
              Management
            </div>
            
            <div className="space-y-4">
              {hasFullAccess && (
                <div className="space-y-2">
                  <Label htmlFor="pic" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    PIC (Person in Charge)
                  </Label>
                  <div className="relative">
                    <MultiSelect
                      options={picOptions}
                      selected={formData.pic}
                      onChange={handlePicChange}
                      placeholder="Select PICs"
                      maxDisplay={3}
                      className="w-full"
                    />
                  </div>
                </div>
              )}
              
              {!hasFullAccess && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    PIC (Person in Charge)
                  </Label>
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded border text-sm">
                    {actualUserName || currentUserName} (You)
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                <div className="space-y-1">
                  <Label htmlFor="integration_enable" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Integration Enable
                  </Label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Enable integration for automated tracking and reporting
                  </p>
                </div>
                <Switch
                  id="integration_enable"
                  checked={formData.integration_enable}
                  onCheckedChange={handleToggleChange}
                  className="data-[state=checked]:bg-blue-600"
                />
              </div>
            </div>
          </div>

          <Separator className="bg-gray-200 dark:bg-gray-600" />

          {/* KOL Selection Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <User className="h-4 w-4 text-blue-500" />
              KOL Selection
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="kol_db_id" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Select Existing KOL
                </Label>
                <div className="relative w-full">
                  <button
                    type="button"
                    onClick={() => setShowKolDropdown(!showKolDropdown)}
                    className={cn(
                      "flex h-10 w-full items-center justify-between rounded-md border border-gray-300 dark:border-gray-600",
                      "bg-white dark:bg-gray-700 px-4 py-2 text-sm text-gray-800 dark:text-gray-100",
                      "ring-offset-background placeholder:text-muted-foreground",
                      "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
                      "disabled:cursor-not-allowed disabled:opacity-50"
                    )}
                  >
                    <span className="truncate">{selectedKolName}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={cn(
                        "h-4 w-4 transition-transform",
                        showKolDropdown ? "rotate-180" : ""
                      )}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  
                  {showKolDropdown && (
                    <div className="absolute z-[99999] w-full mt-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg" style={{ pointerEvents: 'auto' }}>
                      <div className="p-2 border-b border-gray-200 dark:border-gray-600">
                        <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-md">
                          <Search className="h-3.5 w-3.5 mx-2 text-gray-400" />
                          <Input
                            placeholder="Search KOLs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-8 text-sm text-gray-800 dark:text-gray-100"
                            style={{ pointerEvents: 'auto' }}
                          />
                        </div>
                      </div>
                      <ScrollArea className="max-h-[280px] overflow-auto" style={{ pointerEvents: 'auto' }}>
                        <div className="p-1" style={{ pointerEvents: 'auto' }}>
                          {filteredKolOptions.length > 0 ? (
                            filteredKolOptions.map(kol => (
                              <div
                                key={kol.value}
                                className={cn(
                                  "flex items-center justify-between px-3 py-2 cursor-pointer rounded-sm text-sm",
                                  formData.kol_db_id === kol.value 
                                    ? "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" 
                                    : "hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-100"
                                )}
                                onClick={() => handleKolSelect(kol.value)}
                                style={{ pointerEvents: 'auto' }}
                              >
                                <div>
                                  <span className="font-medium">{kol.label}</span>
                                  {kol.social_media_handler && (
                                    <span className="text-gray-500 dark:text-gray-400 ml-1">
                                      (@{kol.social_media_handler.replace('@', '')})
                                    </span>
                                  )}
                                </div>
                                {formData.kol_db_id === kol.value && <Check className="h-4 w-4 text-blue-600" />}
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                              No KOLs found
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Creator Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 h-10"
                  placeholder="Enter creator name"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="channel" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Channel
                </Label>
                <Input
                  id="channel"
                  name="channel"
                  value={formData.channel}
                  onChange={handleInputChange}
                  className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 h-10"
                  placeholder="Enter channel"
                />
              </div>
            </div>
          </div>

          <Separator className="bg-gray-200 dark:bg-gray-600" />

          {/* Campaign Information Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <Briefcase className="h-4 w-4 text-green-500" />
              Campaign Information
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="campaign_bu" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Content ID
                </Label>
                <SearchableSelect
                  options={contentIdOptions}
                  value={formData.campaign_bu}
                  onChange={(value) => handleSelectChange('campaign_bu', value)}
                  placeholder="Select or enter content ID"
                  clearable={true}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tier" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tier
                </Label>
                <SearchableSelect
                  options={TIER_OPTIONS}
                  value={formData.tier}
                  onChange={(value) => handleSelectChange('tier', value)}
                  placeholder="Select tier"
                />
              </div>
            </div>
          </div>

          <Separator className="bg-gray-200 dark:bg-gray-600" />

          {/* Content Details Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <Megaphone className="h-4 w-4 text-green-500" />
              Content Details
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Content Type
                </Label>
                <SearchableSelect
                  options={TYPE_OPTIONS}
                  value={formData.type}
                  onChange={(value) => handleSelectChange('type', value)}
                  placeholder="Select content type"
                  badgeVariant="dark"
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="platform" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Platform
                </Label>
                <SearchableSelect
                  options={PLATFORM_OPTIONS}
                  value={formData.platform}
                  onChange={(value) => handleSelectChange('platform', value)}
                  placeholder="Select platform"
                  badgeVariant="blue"
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2 col-span-2">
                <Label htmlFor="post_link" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Post Link
                  {isCheckingDuplicate && (
                    <Loader2 className="h-3 w-3 ml-2 inline animate-spin text-blue-500" />
                  )}
                </Label>
                <Input
                  id="post_link"
                  name="post_link"
                  value={formData.post_link}
                  onChange={handleInputChange}
                  className={cn(
                    "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 h-10",
                    duplicatePostLink && "border-red-500 dark:border-red-400"
                  )}
                  placeholder="https://..."
                />
                {duplicatePostLink && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    This link already exists in the database
                  </p>
                )}
              </div>
            </div>
          </div>

          <Separator className="bg-gray-200 dark:bg-gray-600" />

          {/* Timeline & Metrics Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <Calendar className="h-4 w-4 text-orange-500" />
              Timeline & Metrics
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="posting_date" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Posting Date
                </Label>
                <Input
                  id="posting_date"
                  name="posting_date"
                  value={postingDate ? new Intl.DateTimeFormat('en-CA', { 
                    year: 'numeric', 
                    month: '2-digit', 
                    day: '2-digit' 
                  }).format(postingDate).replace(/\//g, '-') : ""}
                  onChange={(e) => {
                    try {
                      const dateValue = e.target.value ? new Date(e.target.value) : null;
                      handlePostingDateChange(dateValue);
                    } catch (err) {
                      console.error("Invalid date:", err);
                    }
                  }}
                  type="date"
                  className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 h-10"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="content_views" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Content Views
                </Label>
                <Input
                  id="content_views"
                  name="content_views"
                  type="number"
                  value={formData.content_views}
                  onChange={handleNumberInputChange}
                  className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 h-10"
                  placeholder="0"
                />
              </div>
            </div>
          </div>
        </form>
        
        <DialogFooter className="pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
            disabled={isSubmitting}
            className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || duplicatePostLink || isCheckingDuplicate}
            className="bg-blue-600 hover:bg-blue-700 text-white min-w-[120px] disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              "Add Entry"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
